<template>
  <section
    :class="[
      { active: $store.getters.isAdditionalFilterOpen },
      { 'loading-disabled': isLoading },
      'filter-container',
    ]"
    v-if="filterOptions"
    @click.self="handleCloseModal"
  >
    <div class="filter-panel">
      <!-- Header Section -->
      <div class="filter-header">
        <div class="header-content">
          <div class="header-title">
            <svg class="filter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V6.58579C21 6.851 20.8946 7.10536 20.7071 7.29289L14.2929 13.7071C14.1054 13.8946 14 14.149 14 14.4142V17L10 21V14.4142C10 14.149 9.89464 13.8946 9.70711 13.7071L3.29289 7.29289C3.10536 7.10536 3 6.851 3 6.58579V4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="filter-title">{{ $t('filters') || 'Filters' }}</h3>
          </div>
          <button 
            class="close-btn" 
            :class="{ 'disabled': isLoading }"
            @click="handleCloseModal"
            :disabled="isLoading"
            :aria-label="$t('close') || 'Close'"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div v-if="hasActiveFilters()" class="active-filters-badge">
          <span class="badge-text">{{ $t('active_filters') || 'Active Filters' }}</span>
        </div>
        <div v-if="currentRoomId && currentRoomName" class="room-indicator">
          <svg class="room-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="room-indicator-text">
            {{ $t('viewing_room') || 'Viewing room' }}: <strong>{{ currentRoomName }}</strong>
          </span>
        </div>
      </div>

      <!-- Filter Content -->
      <div class="filter-content">
        <!-- Location Filters Group -->
        <div v-if="hasLocationFilters" class="filter-group">
          <div class="group-header">
            <svg class="group-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" stroke-width="2"/>
              <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="2"/>
            </svg>
            <h4 class="group-title">{{ $t('location') || 'Location' }}</h4>
          </div>
          <div class="filter-fields">
            <FilterSelect
              v-for="field in locationFields"
              :key="field"
              @select="handleSelect"
              @multiSelect="handleMultiSelect"
              :value="filterOptions[field] || []"
              :field="field"
              class="filter-field"
            />
          </div>
        </div>

        <!-- Attributes Filters Group -->
        <div v-if="hasAttributeFilters" class="filter-group">
          <div class="group-header">
            <svg class="group-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h4 class="group-title">{{ $t('attributes') || 'Attributes' }}</h4>
          </div>
          <div class="filter-fields">
            <FilterSelect
              v-if="shouldRenderSelect && filterOptions.gender"
              @select="handleSelect"
              @multiSelect="handleMultiSelect"
              :value="filterOptions.gender"
              field="gender"
              class="filter-field"
            />
            <FilterSelect
              v-if="shouldRenderSelect && fieldsToShow.servikName && filterOptions.servikName && filterOptions.servikName.length"
              @select="handleSelect"
              @multiSelect="handleMultiSelect"
              :value="filterOptions.servikName"
              field="servikName"
              class="filter-field"
            />
          </div>
        </div>

        <!-- Time Filters Group -->
        <div v-if="hasTimeFilters" class="filter-group">
          <div class="group-header">
            <svg class="group-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h4 class="group-title">{{ $t('time') || 'Time' }}</h4>
          </div>
          <div class="filter-fields">
            <FilterSelect
              v-for="field in timeFields"
              :key="field"
              @select="handleSelect"
              @multiSelect="handleMultiSelect"
              :value="filterOptions[field]"
              :field="field"
              class="filter-field"
            />
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="filter-actions">
        <button
          class="action-btn reset-btn"
          @click="clearFilter"
          :disabled="isLoading || (!hasActiveFilters() && !wasFilterApplied())"
        >
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ $t("reset_filter") }}</span>
        </button>
        <button
          class="action-btn apply-btn"
          @click="activateFilter"
          :disabled="isLoading || (!hasActiveFilters() && !wasFilterApplied())"
          :class="{ 'has-filters': hasActiveFilters() || wasFilterApplied() }"
        >
          <svg v-if="!isLoading" class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div v-else class="loading-spinner"></div>
          <span>{{ isLoading ? ($t('applying') || 'Applying...') : $t("filter") }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import FilterSelect from './Filter/FilterSelect';
import { mapGetters } from 'vuex';
import { Toast } from '@/modules/common/Toast';
import { normalizeAnalyticsFilter } from '../services/filterService';

export default {
  data() {
    return {
      shouldRenderSelect: true,
      _menuObserver: null,
      _zIndexUpdateTimeout: null,
      lastAppliedFilters: null, // Track last applied filter state (for comparison)
      savedFilterState: null, // Deep copy of filterSelected when modal opens (last applied state)
      originalBodyOverflow: null // Store original body overflow value
    }
  },
  methods: {
    handleCloseModal() {
      // Prevent closing when filter is applying
      if (this.isLoading) {
        return;
      }
      this.$store.commit({ type: 'toggleAdditionalFilter', value: false });
    },
    handleMultiSelect(value, field) {
      const valueCopy = JSON.parse(JSON.stringify(value));
      this.$store.commit({ type: 'updateFilter', field, value: valueCopy });
    },
    handleSelect(value, field) {
      this.$store.commit({ type: 'updateFilter', field, value });
    },
    async activateFilter() {
      const { roomId } = this.$route.params;
      
      // Check if any filters are selected
      const hasActiveFilters = this.hasActiveFilters();
      const wasApplied = this.wasFilterApplied();
      
      // Allow applying if filters are selected OR if filters were previously applied (to reload with no filters)
      if (!hasActiveFilters && !wasApplied) {
        new Toast({
          message: this.$t('no_filters_selected'),
          type: 'info',
          duration: 3000
        });
        return;
      }
      
      // Create current filter state for comparison
      const currentFilter = {
        ...this.filterSelected,
        roomId: roomId ? roomId : '',
        language: this.$i18n.locale
      };
      
      const currentFilterString = this.normalizeFilter(currentFilter);
      
      // Check if filters are the same as last applied
      if (this.lastAppliedFilters === currentFilterString) {
        // Same filters already applied, no need to reload
        return;
      }
      
      this.$store.commit({ type: 'toggleLoading', value: true });
      
      try {
        await Promise.all([
          this.$store.dispatch({ type: 'getOverviewData', filter: currentFilter }),
          this.$store.dispatch({ type: 'getAnalyticsData', filter: currentFilter })
        ]);
        this.setActiveFilter();
        
        // Store the applied filter state
        this.lastAppliedFilters = currentFilterString;
        
        // Update saved filter state BEFORE closing modal to prevent restoration watcher from restoring old state
        this.savedFilterState = JSON.parse(JSON.stringify(this.filterSelected));
        
        // Close the modal first so Dashboard watchers can detect filter changes
        this.$store.commit({ type: 'toggleAdditionalFilter', value: false });
        
        // Wait for modal to close
        await this.$nextTick();
        
        // Save filter to session storage now that it's been applied
        // This will trigger Dashboard watchers now that modal is closed
        this.$store.commit({ type: 'setFilterSelected', filterSelected: this.filterSelected });
        
        // Set flag to trigger Dashboard update (even if filter values didn't change)
        // This ensures room-specific data is fetched when filters are applied
        this.$store.commit({ type: 'setFilterJustApplied', value: true });
        
        // Wait another tick to ensure watchers have fired and can access the updated filter
        await this.$nextTick();
        
        // Clear the flag after watchers have had a chance to react
        this.$nextTick(() => {
          this.$store.commit({ type: 'setFilterJustApplied', value: false });
        });
        
        // Show success feedback
        new Toast({
          message: this.$t('filter_applied'),
          type: 'success',
          duration: 2000
        });
      } catch (error) {
        console.error('Error applying filter:', error);
      } finally {
        this.$store.commit({ type: 'toggleLoading', value: false });
      }
    },
    hasActiveFilters() {
      const filter = this.filterSelected;
      if (!filter) return false;
      
      // Helper to check if a value is meaningful (not null, not undefined, not empty array, not empty string)
      const hasValue = (value) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim() !== '';
        return true;
      };
      
      return hasValue(filter.building) || 
             hasValue(filter.floor) || 
             hasValue(filter.rooms) || 
             hasValue(filter.cleaners) ||
             hasValue(filter.gender) || 
             hasValue(filter.shift) || 
             hasValue(filter.days) || 
             hasValue(filter.servikName) ||
             hasValue(filter.hour);
    },
    wasFilterApplied() {
      // Check if filters were previously applied (data was loaded with filters)
      return this.$store.getters.isActiveFilter;
    },
    async clearFilter() {
      this.shouldRenderSelect = false;
      
      // Check if filters were previously applied (data was loaded with filters)
      const hadActiveFilters = this.$store.getters.isActiveFilter;
      
      this.resetSelectedFilterProperties();
      
      // Reset last applied filters tracking
      this.lastAppliedFilters = null;
      
      // Only reload data if filters were previously applied
      if (hadActiveFilters) {
        const { roomId } = this.$route.params;
        this.$store.commit({ type: 'toggleLoading', value: true });
        const filter = {
          ...this.filterSelected,
          roomId: roomId ? roomId : '',
          language: this.$i18n.locale
        };
        
        try {
          await Promise.all([
            this.$store.dispatch({ type: 'getOverviewData', filter }),
            this.$store.dispatch({ type: 'getAnalyticsData', filter })
          ]);
          this.setActiveFilter();
          
          // Update last applied filters to reflect cleared state
          this.lastAppliedFilters = this.normalizeFilter(filter);
          
          // Update saved filter state BEFORE closing modal to prevent restoration watcher from restoring old state
          this.savedFilterState = JSON.parse(JSON.stringify(this.filterSelected));
          
          // Close the modal first so Dashboard watchers can detect filter changes
          this.$store.commit({ type: 'toggleAdditionalFilter', value: false });
          
          // Wait for modal to close
          await this.$nextTick();
          
          // Save cleared filter to session storage now that it's been applied
          // This will trigger Dashboard watchers now that modal is closed
          this.$store.commit({ type: 'setFilterSelected', filterSelected: this.filterSelected });
          
          // Set flag to trigger Dashboard update (even if filter values didn't change)
          // This ensures room-specific data is fetched when filters are reset
          this.$store.commit({ type: 'setFilterJustApplied', value: true });
          
          // Wait for watchers to have a chance to react
          await this.$nextTick();
          
          // Clear the flag after watchers have had a chance to react
          this.$nextTick(() => {
            this.$store.commit({ type: 'setFilterJustApplied', value: false });
          });
          
          // Show success feedback
          new Toast({
            message: this.$t('filter_applied') || 'Filter reset',
            type: 'success',
            duration: 2000
          });
        } catch (error) {
          console.error('Error reloading data after filter reset:', error);
        } finally {
          this.$store.commit({ type: 'toggleLoading', value: false });
        }
      } else {
        // Just update the filter status without reloading
        this.setActiveFilter();
      }
      
      setTimeout(() => this.shouldRenderSelect = true, 0);
    },
    setActiveFilter() {
      let filter = JSON.parse(JSON.stringify(this.filterSelected))
      delete filter.company;
      delete filter.time;
      delete filter.viewTimeBy;
      delete filter.timeType;
      delete filter.site;
      delete filter.language;
      const isActive = Object.values(filter).some(element => element !== null)
      return this.$store.commit({ type: 'setFilterStatus', value: isActive })
    },
    resetSelectedFilterProperties() {
      for (const property in this.filterSelected) {
        if (!this.activeFilter[property]) continue
        this.handleSelect(null, property)
      }
      this.handleSelect(null, 'hour')
    },
    hasFilterStateChanged(savedState) {
      // Compare current filter state with saved state
      // Only check properties that are part of the active filter
      const propertiesToCheck = ['building', 'floor', 'rooms', 'cleaners', 'gender', 'shift', 'days', 'servikName', 'hour']
      
      for (const property of propertiesToCheck) {
        const currentValue = this.filterSelected[property]
        const savedValue = savedState[property]
        
        // Normalize values: treat null and empty array as equivalent for multi-select fields
        const normalizeValue = (val, isMultiSelect) => {
          if (isMultiSelect) {
            if (val === null || val === undefined) return []
            if (Array.isArray(val)) return val.length === 0 ? [] : val
            return [val]
          }
          return val
        }
        
        const isMultiSelect = property !== 'hour'
        const normalizedCurrent = normalizeValue(currentValue, isMultiSelect)
        const normalizedSaved = normalizeValue(savedValue, isMultiSelect)
        
        // Compare values (handle arrays specially)
        if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedSaved)) {
          if (normalizedCurrent.length !== normalizedSaved.length) return true
          const currentSorted = [...normalizedCurrent].sort().join(',')
          const savedSorted = [...normalizedSaved].sort().join(',')
          if (currentSorted !== savedSorted) return true
        } else if (normalizedCurrent !== normalizedSaved) {
          return true
        }
      }
      
      return false
    },
    restoreFilterState(savedState) {
      // Set flag to prevent watchers from triggering data reloads during restoration
      this.$store.commit({ type: 'setRestoringFilterState', value: true })
      
      // Step 1: Clear all location fields first to avoid conflicts
      // This ensures we start from a clean state
      if (this.filterSelected.building !== null) {
        this.handleSelect(null, 'building')
      }
      if (this.filterSelected.floor !== null && (Array.isArray(this.filterSelected.floor) ? this.filterSelected.floor.length > 0 : true)) {
        this.handleSelect(null, 'floor')
      }
      if (this.filterSelected.rooms !== null && (Array.isArray(this.filterSelected.rooms) ? this.filterSelected.rooms.length > 0 : true)) {
        this.handleSelect(null, 'rooms')
      }
      if (this.filterSelected.gender !== null && (Array.isArray(this.filterSelected.gender) ? this.filterSelected.gender.length > 0 : true)) {
        this.handleSelect(null, 'gender')
      }
      
      // Step 2: Wait a tick for mutations to complete, then restore in correct order
      this.$nextTick(() => {
        // Restore in order: building/floor first (they clear rooms), then rooms (clears building/gender), then others
        const restoreOrder = ['building', 'floor', 'rooms', 'cleaners', 'gender', 'shift', 'days', 'servikName', 'hour']
        
        restoreOrder.forEach(property => {
          const value = savedState[property]
          const currentValue = this.filterSelected[property]
          
          if (value !== undefined) {
            const isMultiSelect = property !== 'hour'
            
            // Deep copy arrays to avoid reference issues
            if (Array.isArray(value)) {
              if (value.length > 0) {
                this.handleMultiSelect([...value], property)
              } else {
                // Clear array if saved state has empty array and current value is not empty
                if (currentValue !== null && currentValue !== undefined && 
                    (Array.isArray(currentValue) ? currentValue.length > 0 : true)) {
                  this.handleMultiSelect([], property)
                }
              }
            } else if (value !== null) {
              // Single value field
              this.handleSelect(value, property)
            } else {
              // Saved state is null - need to clear current value
              if (currentValue !== null && currentValue !== undefined) {
                if (isMultiSelect) {
                  // For multi-select fields, clear with empty array
                  this.handleMultiSelect([], property)
                } else {
                  // For single-select fields, clear with null
                  this.handleSelect(null, property)
                }
              }
            }
          }
        })
        
        // Clear flag after restoration is complete
        this.$nextTick(() => {
          this.$store.commit({ type: 'setRestoringFilterState', value: false })
        })
      })
    },
    resetAndHideFilter() {
      this.resetSelectedFilterProperties()
      this.$store.commit({ type: 'toggleAdditionalFilter', value: false})
    },
    updateSelectZIndex() {
      // Vue-material renders menus at body level, so we need to target them globally
      if (!this.$store.getters.isAdditionalFilterOpen) return;
      
      // Throttle to prevent performance issues
      if (this._zIndexUpdateTimeout) {
        clearTimeout(this._zIndexUpdateTimeout);
      }
      
      this._zIndexUpdateTimeout = setTimeout(() => {
        const menus = document.querySelectorAll('.md-menu, .md-menu-content, .md-select-menu');
        menus.forEach(menu => {
          if (menu instanceof HTMLElement) {
            menu.style.zIndex = '10001';
            const menuContent = menu.querySelector('.md-menu-content-container');
            if (menuContent instanceof HTMLElement) {
              menuContent.style.zIndex = '10001';
            }
          }
        });
        
        const backdrops = document.querySelectorAll('.md-backdrop');
        backdrops.forEach(backdrop => {
          if (backdrop instanceof HTMLElement) {
            backdrop.style.zIndex = '10000';
          }
        });
        
        this._zIndexUpdateTimeout = null;
      }, 50); // Throttle to 50ms
    },
    normalizeFilter(filter) {
      return normalizeAnalyticsFilter(filter);
    },
    observeMenus() {
      // Disconnect existing observer
      if (this._menuObserver) {
        this._menuObserver.disconnect();
        this._menuObserver = null;
      }
      
      if (!this.$store.getters.isAdditionalFilterOpen) return;
      
      // Use a more targeted observer with debouncing
      let debounceTimer = null;
      
      const observer = new MutationObserver((mutations) => {
        // Only process if menus are actually being added/removed
        const hasMenuChanges = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => 
            node instanceof HTMLElement && 
            (node.classList?.contains('md-menu') || 
             node.classList?.contains('md-menu-content') ||
             node.querySelector?.('.md-menu, .md-menu-content'))
          ) || Array.from(mutation.removedNodes).some(node =>
            node instanceof HTMLElement && 
            (node.classList?.contains('md-menu') || 
             node.classList?.contains('md-menu-content'))
          );
        });
        
        if (hasMenuChanges) {
          // Debounce the update
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(() => {
            this.updateSelectZIndex();
          }, 100);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: false // Only observe direct children, not entire subtree
      });
      
      this._menuObserver = observer;
    }
  },
  // Removed updated() hook - it was causing performance issues and freezing
  // updateSelectZIndex is now only called when needed (on mount, when filter opens, via MutationObserver)
  computed: {
    ...mapGetters(['filterOptions', 'filterSelected', 'isLoading', 'roomIdMap']),
    fieldsToShow() {
      if (['overview', 'suggestion', 'performance', 'analytics', 'cleaningWorkPlan'].includes(this.$route.name)) {
        return {
          building: true,
          floor: true,
          rooms: true,
          cleaners: true,
          gender: true,
          servikName: true,
          shift: true,
          days: true
        }
      } else if (['cleanLog', 'maintenanceLog', 'roomAnalytics', 'roomAnalyticsLog', 'cleanerArrival','roomExposures'].includes(this.$route.name)) {
        return {
          shift: true,
          days: true,
          servikName: true,
        }
      }
      return {}
    },
    hasLocationFilters() {
      return this.fieldsToShow.building || this.fieldsToShow.floor || this.fieldsToShow.rooms || this.fieldsToShow.cleaners;
    },
    hasAttributeFilters() {
      return this.fieldsToShow.gender || this.fieldsToShow.servikName;
    },
    hasTimeFilters() {
      return this.fieldsToShow.shift || this.fieldsToShow.days;
    },
    companyAndSite() {
      return `${this.filterSelected.company}|${this.filterSelected.site}`;
    },
    activeFilter() {
      return this.$store.getters.additionalActiveFilter
    },
    currentRoomId() {
      return this.$route.params.roomId
    },
    currentRoomName() {
      if (!this.currentRoomId) return null
      const roomIdMap = this.$store.getters.roomIdMap
      const room = roomIdMap[this.currentRoomId]
      return room ? room.name : null
    },
    locationFields() {
      if (!this.shouldRenderSelect || !this.filterOptions) return []
      return ['building', 'floor', 'rooms', 'cleaners'].filter((field) => {
        if (field === 'cleaners') return this.fieldsToShow.cleaners
        return this.filterOptions[field]
      })
    },
    timeFields() {
      if (!this.shouldRenderSelect || !this.filterOptions) return []
      return ['shift', 'days'].filter(field => this.filterOptions[field])
    },
  },
  watch: {
    companyAndSite() {
      this.shouldRenderSelect = false;
      setTimeout(() => this.shouldRenderSelect = true, 0);
    },
    "$route.path": function () {
      if (this.$route.path.includes('dashboard')) return
      this.resetAndHideFilter()
    },
    '$store.getters.isAdditionalFilterOpen'(isOpen) {
      if (isOpen) {
        // Save the current filter state (last applied state) when modal opens
        // This allows us to restore it if user closes without applying
        this.savedFilterState = JSON.parse(JSON.stringify(this.filterSelected));
        
        // Save original body overflow value and disable body scroll when modal opens
        this.originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          this.updateSelectZIndex();
          this.observeMenus();
        }, 100);
      } else {
        // Re-enable body scroll when modal closes, restoring original value
        document.body.style.overflow = this.originalBodyOverflow || '';
        this.originalBodyOverflow = null;
        
        // Restore filter state to last applied state if user closed without applying
        // Use nextTick to ensure modal is fully closed and watchers won't fire
        if (this.savedFilterState) {
          this.$nextTick(() => {
            // Only restore if current state differs from saved state (user made unapplied changes)
            if (this.hasFilterStateChanged(this.savedFilterState)) {
              this.restoreFilterState(this.savedFilterState);
            }
            this.savedFilterState = null;
          });
        }
        
        // Disconnect observer when filter closes
        if (this._menuObserver) {
          this._menuObserver.disconnect();
          this._menuObserver = null;
        }
        if (this._zIndexUpdateTimeout) {
          clearTimeout(this._zIndexUpdateTimeout);
          this._zIndexUpdateTimeout = null;
        }
      }
    }
  },
  mounted() {
    // Add global styles for select dropdowns when filter is active
    if (this.$store.getters.isAdditionalFilterOpen) {
      this.updateSelectZIndex();
    }
  },
  beforeDestroy() {
    // Re-enable body scroll if modal was open when component is destroyed
    if (this.$store.getters.isAdditionalFilterOpen) {
      document.body.style.overflow = this.originalBodyOverflow || '';
      this.originalBodyOverflow = null;
    }
    
    if (this._menuObserver) {
      this._menuObserver.disconnect();
      this._menuObserver = null;
    }
    if (this._zIndexUpdateTimeout) {
      clearTimeout(this._zIndexUpdateTimeout);
      this._zIndexUpdateTimeout = null;
    }
  },
  components: { FilterSelect }
};
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.filter-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  max-height: 100vh;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin: 0;
  box-sizing: border-box;
  
  // Ensure it covers the entire viewport
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: -1;
  }
  
  &.active {
    opacity: 1;
    visibility: visible;
  }
  
  &.loading-disabled {
    // Prevent backdrop clicks from closing when loading
    // But allow interactions within the panel
    .filter-panel {
      pointer-events: auto;
    }
  }
}

.filter-panel {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(-20px);
  transition: transform 0.3s ease;
  
  .filter-container.active & {
    transform: translateY(0);
  }
}

.filter-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  
  .header-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .filter-icon {
    width: 24px;
    height: 24px;
    stroke: currentColor;
  }
  
  .filter-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.02em;
  }
  
  .room-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    margin-top: 8px;
    font-size: 14px;
    
    .room-icon {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      flex-shrink: 0;
    }
    
    .room-indicator-text {
      display: flex;
      align-items: center;
      gap: 4px;
      
      strong {
        font-weight: 600;
      }
    }
  }
  
  .close-btn {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
    
    svg {
      width: 20px;
      height: 20px;
    }
    
    &:hover:not(.disabled) {
      background: rgba(255, 255, 255, 0.3);
      transform: rotate(90deg);
    }
    
    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
  
  .active-filters-badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    backdrop-filter: blur(10px);
  }
}

.filter-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.filter-group {
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  .group-icon {
    width: 20px;
    height: 20px;
    stroke: #667eea;
    flex-shrink: 0;
  }
  
  .group-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    letter-spacing: -0.01em;
  }
  
  .filter-fields {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
}

.filter-actions {
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  .btn-icon {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    flex-shrink: 0;
  }
  
  &.reset-btn {
    background: #f1f5f9;
    color: #64748b;
    
    &:hover:not(:disabled) {
      background: #e2e8f0;
      color: #475569;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  &.apply-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(102, 126, 234, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(102, 126, 234, 0.4);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &.has-filters {
      animation: pulse 2s infinite;
    }
  }
  
  .loading-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 4px 6px -1px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 4px 6px -1px rgba(102, 126, 234, 0.5), 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Responsive design
@media (max-width: 768px) {
  .filter-container {
    padding: 0;
    align-items: stretch;
  }
  
  .filter-panel {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
  
  .filter-content {
    padding: 20px 16px;
  }
  
  .filter-fields {
    grid-template-columns: 1fr;
  }
  
  .filter-actions {
    flex-direction: column-reverse;
    
    .action-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
